import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { id } from "date-fns/locale";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Printer, Download, ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";

// =================================================================
// KOMPONEN TANDA TERIMA (A5 = SETENGAH A4)
// =================================================================
const TandaTerimaTemplate = ({ invoice }) => {
    const invoiceDate = invoice.invoice_date || invoice.date;
    const formattedDate = invoiceDate
        ? format(new Date(invoiceDate), 'dd MMMM yyyy', { locale: id })
        : format(new Date(), 'dd MMMM yyyy', { locale: id });

    return (
        <div
            className="w-full h-full bg-white relative box-border"
            style={{
                padding: '12mm 20mm',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* LOGO */}
            <div style={{ marginBottom: '8px' }}>
                <img src="/logo.png" alt="Logo OCS" style={{ height: '48px', objectFit: 'contain' }} />
            </div>

            {/* JUDUL */}
            <h2 style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '18px',
                textTransform: 'uppercase',
                textDecoration: 'underline',
                letterSpacing: '3px',
                marginBottom: '20px',
                color: 'black'
            }}>
                Tanda Terima
            </h2>

            {/* INFO CUSTOMER */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '100px 10px 1fr',
                gap: '4px 4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'black',
                marginBottom: '20px'
            }}>
                <div>UNTUK</div><div>:</div>
                <div style={{ textTransform: 'uppercase' }}>
                    {invoice.customer_name} ( {invoice.customer_id} )
                </div>
                <div>ALAMAT</div><div>:</div>
                <div style={{ textTransform: 'uppercase', lineHeight: '1.6' }}>
                    {invoice.customer_building && <>{invoice.customer_building}<br /></>}
                    {invoice.customer_address1 && <>{invoice.customer_address1}<br /></>}
                    {invoice.customer_address2 && <>{invoice.customer_address2}<br /></>}
                    {invoice.customer_city}{invoice.customer_post_code ? ` ${invoice.customer_post_code}` : ''}
                </div>
            </div>

            {/* NO KWITANSI */}
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'black', textDecoration: 'underline', marginBottom: '8px' }}>
                NO KWITANSI
            </div>
            <div style={{
                display: 'grid',
                gridTemplateColumns: '25px 140px 1fr',
                gap: '6px 4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'black',
                marginBottom: '24px'
            }}>
                <div>1.</div>
                <div>{invoice.invoice_number}</div>
                <div>Document / Airfreight Shipment Charge</div>
                <div>2.</div>
                <div>NPWP :</div>
                <div>{invoice.customer_npwp || '-'}</div>
            </div>

            {/* TANDA TANGAN - rata bawah */}
            <div style={{
                marginTop: 'auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'black'
            }}>
                {/* PENERIMA */}
                <div style={{ width: '180px', textAlign: 'center' }}>
                    <p style={{ marginBottom: '8px', visibility: 'hidden' }}>placeholder</p>
                    <p style={{ marginBottom: '96px' }}>Penerima</p>
                    <div style={{ borderBottom: '1px dashed #666', width: '100%' }}></div>
                </div>

                {/* PENGIRIM */}
                <div style={{ width: '200px', textAlign: 'center' }}>
                    <p style={{ textAlign: 'right', fontWeight: 'normal', marginBottom: '8px' }}>
                        Jakarta, {formattedDate}
                    </p>
                    <p style={{ marginBottom: '96px' }}>Pengirim</p>
                    <div style={{ borderBottom: '1px dashed #666', width: '100%' }}></div>
                </div>
            </div>
        </div>
    );
};

// =================================================================
// HALAMAN RECEIPT
// =================================================================
const ReceiptPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Ambil data dari navigation state
    const invoices = location.state?.invoices || [];

    useEffect(() => {
        if (invoices.length === 0) {
            toast.error("Tidak ada data tanda terima!");
            navigate(-1);
        }
    }, [invoices, navigate]);

    // Group 2 invoice per halaman A4
    const pages = [];
    for (let i = 0; i < invoices.length; i += 2) {
        pages.push(invoices.slice(i, i + 2));
    }

    const handlePrint = () => {
        setTimeout(() => window.print(), 100);
    };

    const handleDownloadPDF = async () => {
        const element = document.getElementById("receipt-print-area");
        const printPages = element.querySelectorAll('.receipt-page');

        if (!printPages || printPages.length === 0) {
            return toast.error("Tidak ada halaman untuk didownload");
        }

        const invNumbers = invoices.map(inv => inv.invoice_number).filter(Boolean);
        const fileName = invNumbers.length === 1
            ? `Tanda_Terima_${invNumbers[0]}.pdf`
            : `Tanda_Terima_${invNumbers.join('_')}.pdf`;

        const loadToast = toast.loading(`Menyiapkan ${fileName}...`);
        setIsGeneratingPDF(true);

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');

            for (let i = 0; i < printPages.length; i++) {
                const canvas = await html2canvas(printPages[i], {
                    scale: 4,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                });

                const imgData = canvas.toDataURL("image/jpeg", 0.95);
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);

                await new Promise(resolve => setTimeout(resolve, 30));
            }

            pdf.save(fileName);
            toast.success(`Berhasil download: ${fileName}`, { id: loadToast });
        } catch (error) {
            console.error("PDF Error:", error);
            toast.error("Gagal download PDF", { id: loadToast });
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    if (invoices.length === 0) return null;

    return (
        <>
            {/* PRINT STYLES - KHUSUS RECEIPT */}
            <style>{`
                @page {
                    size: A4 portrait;
                    margin: 0 !important;
                }
                @media print {
                    body * { visibility: hidden !important; }
                    
                    #receipt-print-area,
                    #receipt-print-area * {
                        visibility: visible !important;
                    }
                    
                    #receipt-print-area {
                        position: fixed !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 210mm !important;
                        background: white !important;
                        overflow: visible !important;
                    }

                    .receipt-page {
                        width: 210mm !important;
                        height: 296mm !important;
                        page-break-after: always !important;
                        page-break-inside: avoid !important;
                        display: block !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        box-sizing: border-box !important;
                        overflow: hidden !important;
                    }

                    .receipt-toolbar {
                        display: none !important;
                    }
                }
            `}</style>

            <div className="min-h-screen bg-gray-600">

                {/* TOOLBAR */}
                <div className="receipt-toolbar bg-white w-full max-w-[210mm] mx-auto p-4 flex justify-between items-center sticky top-0 shadow-lg z-50">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-bold transition"
                        >
                            <ArrowLeft size={18} /> Kembali
                        </button>
                        <span className="text-gray-300">|</span>
                        <h3 className="font-bold text-gray-800">
                            Preview Tanda Terima ({invoices.length} Invoice)
                        </h3>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleDownloadPDF}
                            disabled={isGeneratingPDF}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-bold shadow-sm transition"
                        >
                            {isGeneratingPDF
                                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <Download size={18} />
                            }
                            Download PDF
                        </button>
                        <button
                            onClick={handlePrint}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-bold shadow-sm transition"
                        >
                            <Printer size={18} /> Print Fisik
                        </button>
                    </div>
                </div>

                {/* AREA CETAK */}
                <div id="receipt-print-area" className="w-full flex flex-col items-center py-6">
                    <div className="shadow-2xl max-w-[210mm] w-full bg-white flex flex-col">
                        {pages.map((group, pageIndex) => (
                            <div
                                key={pageIndex}
                                className="receipt-page bg-white w-full"
                                style={{ height: '296mm', boxSizing: 'border-box', overflow: 'hidden' }}
                            >
                                {/* TANDA TERIMA ATAS */}
                                <div style={{ height: '50%', borderBottom: '1px dashed #999', overflow: 'hidden' }}>
                                    <TandaTerimaTemplate invoice={group[0]} />
                                </div>

                                {/* TANDA TERIMA BAWAH (kalau ada) */}
                                {group[1] ? (
                                    <div style={{ height: '50%', overflow: 'hidden' }}>
                                        <TandaTerimaTemplate invoice={group[1]} />
                                    </div>
                                ) : (
                                    <div style={{ height: '50%' }}></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReceiptPage;